// "See all" popup of the scene-1 pause chat: every org style / visual format as a static grid (like the console newchat pick
// screens). Click = highlight (the layout never moves), the confirm button picks it; the pick then leads to the chat's confirm card.
import React, { useEffect, useState } from "react";
import { LuLayoutTemplate, LuPalette } from "react-icons/lu";
import i18n from "../../../locales/i18n";
import AppModal from "../common/modal/AppModal";
import { catalogThumb } from "./catalogThumb";
import { fetchPickOptions } from "./buildApi";

const t = (k, o) => i18n.t(`aideaChat.buildNotice.${k}`, o);

// Image with an icon fallback (no preview, or it failed to load).
export function Thumb({ src, kind, className }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed)
    return <img src={src} alt="" className={className} onError={() => setFailed(true)} />;
  const Icon = kind === "style" ? LuPalette : LuLayoutTemplate;
  return (
    <div
      className={`${className} tw-flex tw-items-center tw-justify-center tw-bg-[var(--ac-hover)] tw-text-[var(--ac-muted)]`}
      data-testid="thumb-fallback"
    >
      <Icon size={20} />
    </div>
  );
}

const RecBadge = () => (
  <span className="tw-rounded-full tw-bg-[var(--ac-accent-tint)] tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-medium tw-text-[var(--ac-accent)]">
    {t("recommended")}
  </span>
);

// recommended: { style: styleId, format: slug } of the chat reply. onPick(option) gets the same shape as the reply's option cards.
export default function BuildPickModal({
  show,
  onClose,
  onPick,
  initialKind = "style",
  recommended = {},
}) {
  const [kind, setKind] = useState(initialKind);
  const [lists, setLists] = useState({}); // kind → array | "error"
  const [sel, setSel] = useState(null); // highlighted item
  const [variant, setVariant] = useState(1);
  useEffect(() => {
    if (!show || lists[kind]) return;
    let off = false;
    fetchPickOptions(window.idealStudioTrainingId, kind).then(
      (l) => !off && setLists((x) => ({ ...x, [kind]: l })),
      () => !off && setLists((x) => ({ ...x, [kind]: "error" }))
    );
    return () => {
      off = true;
    };
  }, [show, kind, lists]);
  const switchKind = (k) => {
    setKind(k);
    setSel(null);
    setVariant(1);
  };
  const raw = lists[kind];
  const items = Array.isArray(raw)
    ? [...raw].sort((a, b) => Number(isRec(b)) - Number(isRec(a)))
    : null;
  function isRec(i) {
    return kind === "style" ? i.styleId === recommended.style : i.slug === recommended.format;
  }
  const name = (i) => (kind === "style" ? i.label : i.name);
  const thumb = (i, v = 1) => (kind === "style" ? i.previewUrl : catalogThumb(i.slug, v));
  const confirm = () => {
    if (!sel) return;
    onPick(
      kind === "style"
        ? { kind: "style", styleId: sel.styleId, styleSource: sel.styleSource, label: sel.label }
        : { kind: "format", slug: sel.slug, variant, label: sel.name }
    );
  };
  return (
    <AppModal
      show={show}
      onClose={onClose}
      title={t("seeAllTitle")}
      size="xl"
      centered
      backdrop
      keyboard
      showSceneToggle={false}
      footer={
        <button type="button" className="aidea-build-btn" disabled={!sel} onClick={confirm}>
          {sel ? t("pickConfirm", { name: name(sel) }) : t("optionSelect")}
        </button>
      }
    >
      <div role="tablist" className="tw-mb-3 tw-flex tw-gap-2">
        {["style", "format"].map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={kind === k}
            className={`aidea-build-btn ${kind === k ? "" : "aidea-build-btn-ghost"}`}
            onClick={() => switchKind(k)}
          >
            {t(k === "style" ? "tabStyles" : "tabFormats")}
          </button>
        ))}
      </div>
      {/* Fixed-height preview of the highlighted card: picking never reflows the grid below. */}
      <div
        className="tw-mb-3 tw-flex tw-h-[200px] tw-items-center tw-gap-4 tw-rounded-xl tw-p-3 tw-ring-1 tw-ring-[var(--ac-border)]"
        data-testid="pick-preview"
      >
        {sel ? (
          <>
            <Thumb
              src={thumb(sel, variant)}
              kind={kind}
              className="tw-h-full tw-w-[280px] tw-flex-none tw-rounded-lg tw-object-cover"
            />
            <div className="tw-min-w-0 tw-flex-1 tw-text-[13px]">
              <div className="tw-text-[15px] tw-font-medium">{name(sel)}</div>
              {kind === "format" && <div className="tw-mt-1">{sel.whenToUse}</div>}
              {kind === "format" && sel.variants && sel.variants.length > 1 && (
                <div className="tw-mt-2 tw-flex tw-gap-2">
                  {sel.variants.map((v) => (
                    <button
                      key={v.n}
                      type="button"
                      aria-pressed={variant === v.n}
                      title={t("variantN", { n: v.n })}
                      className={`tw-rounded-md tw-border-0 tw-bg-transparent tw-p-0 ${variant === v.n ? "tw-ring-2 tw-ring-[var(--ac-accent)]" : "tw-ring-1 tw-ring-[var(--ac-border)]"}`}
                      onClick={() => setVariant(v.n)}
                    >
                      <Thumb
                        src={catalogThumb(sel.slug, v.n)}
                        kind="format"
                        className="tw-h-12 tw-w-20 tw-rounded-md tw-object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="tw-text-[13px] tw-text-[var(--ac-muted)]">{t("seeAllTitle")}</div>
        )}
      </div>
      {raw === "error" && (
        <div role="alert" className="aidea-build-notice-error">
          {t("pickFailed")}
        </div>
      )}
      {!raw && <div>{t("pickLoading")}</div>}
      {items && items.length === 0 && <div>{t("pickEmpty")}</div>}
      {items && items.length > 0 && (
        <div className="tw-grid tw-grid-cols-2 tw-gap-3 md:tw-grid-cols-3 lg:tw-grid-cols-4">
          {items.map((i) => {
            const id = kind === "style" ? i.styleId : i.slug;
            const on = sel && (kind === "style" ? sel.styleId === id : sel.slug === id);
            return (
              <button
                key={id}
                type="button"
                aria-pressed={!!on}
                className={`tw-flex tw-flex-col tw-gap-1 tw-rounded-xl tw-border-0 tw-bg-transparent tw-p-2 tw-text-left ${on ? "tw-ring-2 tw-ring-[var(--ac-accent)]" : "tw-ring-1 tw-ring-[var(--ac-border)]"}`}
                onClick={() => {
                  setSel(i);
                  setVariant(1);
                }}
              >
                <Thumb
                  src={thumb(i)}
                  kind={kind}
                  className="tw-h-24 tw-w-full tw-rounded-md tw-object-cover"
                />
                <span className="tw-text-[13px] tw-font-medium">{name(i)}</span>
                {isRec(i) && <RecBadge />}
              </button>
            );
          })}
        </div>
      )}
    </AppModal>
  );
}
